import React, {
  useState,
  useRef,
  useEffect,
  ForwardedRef,
  forwardRef,
} from "react";
import classNames from "classnames";
import styles from "./TextArea.module.scss";

interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  value: string;
  onChange: (...event: any[]) => void;
  onBlur?: () => void;
  name: string;
  maxLength?: number;
  label?: string;
  showError?: boolean;
  errorMessage?: string;
  showLabel?: boolean;
  showMaxLength?: boolean;
}

const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      name,
      value,
      onChange,
      onBlur,
      error,
      maxLength = 2000,
      label,
      showError,
      errorMessage,
      showLabel = false,
      showMaxLength = true,
      ...rest
    },
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
      const clickFocus = (e: MouseEvent) => {
        if (
          wrapperRef.current &&
          wrapperRef.current.contains(e.target as Node)
        ) {
          (ref as React.RefObject<HTMLTextAreaElement>)?.current?.focus?.();
        }
      };
      document.addEventListener("mousedown", clickFocus);
      return () => document.removeEventListener("mousedown", clickFocus);
    }, [ref]);

    const handleFocus = () => setFocused(true);
    const handleBlurCapture = () => setFocused(false);

    // 🛡 Prevent leading space on typing
    const handleBeforeInput = (
      e: React.FormEvent<HTMLTextAreaElement> & { data: string }
    ) => {
      const input = e.currentTarget;
      const inputChar = e.data;

      // Prevent leading space on empty value
      if (inputChar === " " && value.length === 0) {
        e.preventDefault();
        return;
      }

      // Prevent replacing all text with a space
      const isFullSelection =
        input.selectionStart === 0 && input.selectionEnd === input.value.length;

      if (inputChar === " " && isFullSelection) {
        e.preventDefault();
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const trimmedValue = e.target.value.replace(/\s+$/, "");

      if (trimmedValue !== value) {
        onChange({
          ...e,
          target: {
            ...e.target,
            name,
            value: trimmedValue,
          },
        });
      }

      onBlur?.();
    };

    // 🧼 Prevent leading space on paste
    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pasted = e.clipboardData.getData("text");
      const trimmed = pasted.replace(/^\s+/, "");

      if (trimmed !== pasted) {
        e.preventDefault();

        const target = e.target as HTMLTextAreaElement;
        const selectionStart = target.selectionStart ?? 0;
        const selectionEnd = target.selectionEnd ?? 0;

        // Replace the selected text with the trimmed paste
        const newValue =
          value.slice(0, selectionStart) + trimmed + value.slice(selectionEnd);

        onChange({
          ...e,
          target: {
            ...e.target,
            name,
            value: newValue,
          },
        });
      }
    };

    return (
      <div className={styles.outer}>
        <div
          ref={wrapperRef}
          className={classNames(styles.container, {
            [styles.focused]: focused,
            [styles.error]: !!error || value.length > maxLength,
          })}>
          {(showLabel || (!value && !focused)) && (
            <label className={styles.label}>{label}</label>
          )}
          <textarea
            ref={ref}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onBlurCapture={handleBlurCapture}
            onBeforeInput={handleBeforeInput}
            onPaste={handlePaste}
            className={classNames(styles.textarea, {
              [styles.maxReached]: value.length > maxLength,
              [styles.withLabel]: showLabel,
            })}
            rows={4}
            {...rest}
          />
        </div>
        {(showError || showMaxLength) && (
          <div className={styles.footer}>
            {showMaxLength && (
              <p
                className={classNames(styles.charCount, {
                  [styles.maxReachedText]: value.length > maxLength,
                })}>
                {value?.length} / <span>{maxLength}</span>
              </p>
            )}
            {error && <p className={styles.errorText}>{error}</p>}
            {!error && errorMessage && value.length > maxLength && (
              <p className={styles.errorMessage}>{errorMessage}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";
export default TextArea;
